import { CSSProperties } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { alpha, Checkbox, FormControlLabel, styled, Typography } from '@mui/material';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';

const CheckboxCard = styled('label', {
  shouldForwardProp: (prop) => prop !== 'checkedState' && prop !== 'isDisabled',
})<{ checkedState?: boolean; isDisabled?: boolean }>(({ theme, checkedState, isDisabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
  padding: theme.spacing(1.5, 1.75),
  borderRadius: '12px',
  border: `1px solid ${checkedState ? theme.palette.accent.main : theme.palette.divider}`,
  backgroundColor: checkedState ? alpha(theme.palette.accent.main, 0.06) : alpha(theme.palette.primary.main, 0.04),
  cursor: isDisabled ? 'default' : 'pointer',
  textAlign: 'left',
  opacity: isDisabled ? 0.55 : 1,
  transition: theme.transitions.create(['border-color', 'background-color']),
  '&:hover': isDisabled
    ? undefined
    : { borderColor: checkedState ? theme.palette.accent.main : theme.palette.primary.main },
  '&:focus-within': {
    borderColor: checkedState ? theme.palette.accent.main : theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '& .checkbox-card-glyph': { flex: 'none', fontSize: 26 },
}));

const accessibilityOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const CheckboxWidget = ({
  id,
  value,
  disabled,
  readonly,
  label,
  hideLabel,
  onChange,
  options,
  schema,
}: WidgetProps) => {
  const checked = Boolean(value);
  const isDisabled = disabled || readonly;
  const description = ((options?.description as string) || schema?.description) ?? '';

  if (description) {
    return (
      <CheckboxCard checkedState={checked} isDisabled={isDisabled}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={isDisabled}
          onChange={(event) => onChange(event.target.checked)}
          style={accessibilityOnly}
        />
        <div className="min-w-0 flex-1">
          {!hideLabel && <Typography sx={{ fontWeight: 600 }}>{label}</Typography>}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </div>
        {checked ? (
          <CheckBoxRoundedIcon className="checkbox-card-glyph" sx={{ color: 'accent.main' }} aria-hidden />
        ) : (
          <CheckBoxOutlineBlankRoundedIcon
            className="checkbox-card-glyph"
            sx={{ color: 'text.secondary' }}
            aria-hidden
          />
        )}
      </CheckboxCard>
    );
  }

  return (
    <FormControlLabel
      control={
        <Checkbox
          id={id}
          checked={checked}
          disabled={isDisabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={hideLabel ? undefined : label}
    />
  );
};

export default CheckboxWidget;
